import React, { useCallback } from 'react';
import { GetDocsAndFormsSearchDocument } from '__generated__/graphql';
import { DocsAndFormsResult } from 'components/docs-and-forms/search/documents/DocsAndFormsResult';
import { TypedSearchResults } from '../TypedSearchResults';
import { EmptyResults } from '../EmptyResults';
import TitledPage from 'components/common/TitledPage';
import {
  StyledSearchContent,
  StyledSearchHeader,
  StyledSearchResults,
} from '../SearchStyles';
import { SearchFilters } from '../SearchFilters';
import useSearchVariables from '../useSearchVariables';
import { FormsFilters } from './FormsFilters';
import { FilterChips } from '../FilterChips';
import { FlashMessages } from 'components/common/FlashMessages';
import styled from 'styled-components';

export function FormsSearch() {
  const mutateVariables: NonNullable<Parameters<typeof useSearchVariables>[0]> =
    useCallback(
      (variables, queryString) => ({
        ...variables,
        practiceAreas: queryString.practiceAreas?.split(',') || [],
        trueFormSubtypes: queryString.trueFormSubtypes?.split(',') || [],
        courtIds: queryString.courtIds?.split(',') || [],
      }),
      [],
    );

  const variables = useSearchVariables(mutateVariables);
  const filters = React.useMemo(() => {
    return {
      'Practice Area': {
        hasChip: (searchParams: any) =>
          searchParams.get('practiceAreas') !== null,
        onRemove: (searchParams: any) => {
          searchParams.delete('practiceAreas');
        },
      },
      Type: {
        hasChip: (searchParams: any) =>
          searchParams.get('trueFormSubtypes') !== null,
        onRemove: (searchParams: any) => {
          searchParams.delete('trueFormSubtypes');
        },
      },
      County: {
        hasChip: (searchParams: any) => searchParams.get('courtIds') !== null,
        onRemove: (searchParams: any) => {
          searchParams.delete('courtIds');
        },
      },
    };
  }, []);

  return (
    <TitledPage title="Search Documents & Forms">
      <SearchFilters queryVariables={['textFilters', 'optionFilters']}>
        <FormsFilters />
      </SearchFilters>

      <FlashMessages />
      <StyledSearchContent>
        <StyledSearchHeader>
          <div>
            <FilterChips filters={filters} />
          </div>
        </StyledSearchHeader>
        <StyledFormResults>
          <TypedSearchResults
            resultTitle="Documents & Forms Result"
            query={GetDocsAndFormsSearchDocument}
            variables={variables}
            alwaysSearch
            emptyResults={<EmptyResults />}
          >
            {result => (
              <DocsAndFormsResult key={result!.contentfulId} result={result!} />
            )}
          </TypedSearchResults>
        </StyledFormResults>
      </StyledSearchContent>
    </TitledPage>
  );
}

const StyledFormResults = styled(StyledSearchResults)`
  padding-top: calc(60px - 38px);
`;
